import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { BreadcrumbItem } from '../../types/navigation';

/**
 * Breadcrumbs Component - Molecular Level
 * 
 * Navigation breadcrumbs for showing user's location in the app
 * 
 * Features:
 * - Automatic breadcrumb generation from route
 * - Custom breadcrumb items support
 * - Home icon for root
 * - Accessible navigation
 */

export interface BreadcrumbsProps {
  /** Custom breadcrumb items (overrides automatic generation) */
  items?: BreadcrumbItem[];
  /** Separator character or element */
  separator?: React.ReactNode;
  /** Show home icon */
  showHome?: boolean;
}

const defaultSeparator = (
  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const homeIcon = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
  </svg>
);

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items: itemsProp,
  separator = defaultSeparator,
  showHome = true,
}) => {
  const location = useLocation();

  // Generate breadcrumbs from current path if items not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (itemsProp) return itemsProp;

    const pathnames = location.pathname.split('/').filter(x => x);
    const breadcrumbs: BreadcrumbItem[] = [];

    if (showHome) {
      breadcrumbs.push({
        label: 'Home',
        /** Main app shell: index redirects to the default dashboard view (e.g. analytics). */
        path: '/dashboard',
      });
    }

    pathnames.forEach((name, index) => {
      const path = `/${pathnames.slice(0, index + 1).join('/')}`;
      const label = name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
      
      breadcrumbs.push({
        label,
        path,
        active: index === pathnames.length - 1,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <nav aria-label="Breadcrumb" className="flex items-center space-x-1">
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;

        return (
          <React.Fragment key={item.path || index}>
            {index > 0 && (
              <span className="flex-shrink-0" aria-hidden="true">
                {separator}
              </span>
            )}
            
            {isLast || !item.path ? (
              <span
                className="text-sm font-medium text-gray-700 flex items-center gap-1"
                aria-current="page"
              >
                {index === 0 && showHome ? homeIcon : null}
                {item.label}
              </span>
            ) : (
              <Link
                to={item.path}
                className="text-sm font-medium text-gray-500 hover:text-[#EAB308] transition-colors flex items-center gap-1"
              >
                {index === 0 && showHome ? homeIcon : null}
                {item.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default Breadcrumbs;

